import { ChangeEvent, useCallback, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Modal,
  Select,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { DrawerForm } from "../../Common/styles/drawer";
import { Delete } from "@mui/icons-material";
import { instrumentUploadFile } from "../../../utils/upload";
import { DISEASE_AREAS, MAX_FILE_SIZE_5MB } from "../../../utils/instruments";
import TipTapFormik from "../../Tiptap/TipTapFormik";

let schema = yup.object().shape({
  title: yup.string().required("Title is Required").trim(),
  diseaseArea: yup.string().required("Disease Area is Required").trim(),
  source: yup.string().required("Source / Location is Required").trim(),
  cost: yup.string().required("Approximate Cost is Required").trim(),
  fileUrl: yup.string().required("File is Required").trim(),
  description: yup.string().required("Description is Required"),
});

const BoxStyle: SxProps = {
  width: "54px",
  height: "54px",
  borderRadius: "8px",
  border: 1,
  borderColor: "#E3F1F4",
  background: "#ffffff",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

type Props = {
  showModal: boolean;
  closeModal: () => void;
  data: any;
  refreshPage: () => void;
};

const AddInstrumentModal = ({
  showModal,
  closeModal,
  data,
  refreshPage,
}: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      let res: AxiosResponse;
      if (data?.id) {
        res = await http.patch(`/instrument-library/${data?.id}`, values);
      } else {
        res = await http.post(`/instrument-library`, values);
      }

      toastMessage("success", res?.data?.message);
      closeModal();
      setSubmitLoader(false);
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  const uploadFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>, setFieldValue: any) => {
      try {
        if (e.target.files && e.target.files.length > 0) {
          const files = e.target.files;
          if (files && files.length > 0) {
            const file = files[0];
            if (file.size > MAX_FILE_SIZE_5MB) {
              toastMessage("warning", "File size must be smaller than 5MB!");
              return;
            }
            setLoading(true);
            const obj = await instrumentUploadFile(file);
            if (obj.postUploadImageUrl) {
              setFieldValue("fileUrl", obj.postUploadImageUrl);
            }
            setLoading(false);
          }
        }
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    },
    [setLoading]
  );

  const clearFile = (setFieldValue: any) => {
    setFieldValue("fileUrl", "");
    setFieldValue("fileName", "");
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={data?.id ? "Edit Artifact" : "Add Artifact"}
          onCloseClick={closeModal}
        />
        <Formik
          initialValues={{
            title: data?.title || "",
            diseaseArea: data?.diseaseArea || "",
            source: data?.source || "",
            cost: data?.cost || "",
            fileUrl: data?.fileUrl || "",
            description: data?.description || "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <Box>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="title">
                    Title <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Title"
                    id="title"
                    {...getFieldProps("title")}
                    error={touched?.title && errors?.title ? true : false}
                    helperText={
                      touched?.title && errors?.title
                        ? (errors?.title as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="disease">
                    Disease Area <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    id="disease"
                    fullWidth
                    value={values.diseaseArea || ""}
                    onChange={(e) => {
                      setFieldValue("diseaseArea", e.target.value);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: "300px", overflowY: "auto" },
                      },
                    }}
                  >
                    {DISEASE_AREAS?.map((option: any) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={
                      touched?.diseaseArea && errors?.diseaseArea ? true : false
                    }
                  >
                    {touched?.diseaseArea && errors?.diseaseArea
                      ? (errors?.diseaseArea as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="source">
                      Source / Location <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <TextField
                      placeholder="Source / Location"
                      id="source"
                      {...getFieldProps("source")}
                      error={touched?.source && errors?.source ? true : false}
                      helperText={
                        touched?.source && errors?.source
                          ? (errors?.source as string)
                          : " "
                      }
                    />
                  </FormControl>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="cost">
                      Approximate Cost <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <TextField
                      placeholder="Approximate Cost"
                      id="cost"
                      {...getFieldProps("cost")}
                      error={touched?.cost && errors?.cost ? true : false}
                      helperText={
                        touched?.cost && errors?.cost
                          ? (errors?.cost as string)
                          : " "
                      }
                    />
                  </FormControl>
                </Box>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="description">
                    Description <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TipTapFormik
                    name="description"
                    setFieldValue={setFieldValue}
                  />
                  <FormHelperText
                    error={
                      touched?.description && errors?.description ? true : false
                    }
                  >
                    {touched?.description && errors?.description
                      ? (errors?.description as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={{ ...InputWrapper, mb: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="file-upload">
                    File Upload <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {values.fileUrl ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="subtitle1">
                          File Available
                        </Typography>
                        <Box sx={BoxStyle}>
                          <IconButton onClick={() => clearFile(setFieldValue)}>
                            <Delete color="error" />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        {loading ? (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <CircularProgress size={25} />
                          </Box>
                        ) : (
                          <Button
                            variant="outlined"
                            component="label"
                            sx={{ height: "54px" }}
                          >
                            Upload File
                            <input
                              hidden
                              accept="text/csv"
                              type="file"
                              onChange={(e) => uploadFile(e, setFieldValue)}
                            />
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                  <FormHelperText
                    error={touched?.fileUrl && errors?.fileUrl ? true : false}
                  >
                    {touched?.fileUrl && errors?.fileUrl
                      ? (errors?.fileUrl as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        Save
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddInstrumentModal;
